import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import { useMe } from '@/api/session'
import type { PayPage } from '@/api/types'
import { Footer } from '@/components/Footer'
import { PublicHeader } from '@/components/PublicHeader'
import { money } from '@/lib/money'
import { relativeDays, shortDate } from '@/lib/dates'
import { tokens } from '@/theme'

/**
 * The pay page.
 *
 * The busiest screen in the product and the only one that has to work for
 * somebody who has never heard of us, arriving from an email on a phone on a
 * bad network. So: one column, one number, one button, and nothing above the
 * fold that is not needed to pay.
 *
 * Everything here came down the wire already filtered — no roster, nobody
 * else's amount. The server decides that, not this file.
 */
export function Pay() {
  const { token = '' } = useParams()
  const [params] = useSearchParams()
  const queryClient = useQueryClient()

  const page = useQuery({
    queryKey: ['pay', token],
    queryFn: () => api.get<PayPage>(`/api/pay/${token}`),
  })

  const claim = useMutation({
    mutationFn: () => api.post<PayPage>(`/api/pay/${token}/claim`, {}),
    onSuccess: (data) => queryClient.setQueryData(['pay', token], data),
  })

  const optOut = useMutation({
    mutationFn: () =>
      api.post<PayPage>(`/api/pay/${token}/sms-consent/withdraw`),
    onSuccess: (data) => queryClient.setQueryData(['pay', token], data),
  })

  const unclaim = useMutation({
    mutationFn: () => api.post<PayPage>(`/api/pay/${token}/unclaim`),
    onSuccess: (data) => queryClient.setQueryData(['pay', token], data),
  })

  const notMine = useMutation({
    mutationFn: (on: boolean) =>
      api.post<PayPage>(`/api/pay/${token}/not-mine?on=${on}`),
    onSuccess: (next) => queryClient.setQueryData(['pay', token], next),
  })

  const mute = useMutation({
    mutationFn: (on: boolean) =>
      api.post<PayPage>(`/api/pay/${token}/mute?on=${on}`),
    onSuccess: (data) => queryClient.setQueryData(['pay', token], data),
  })

  /**
   * One click from the email: `?sent=1` marks it as sent on arrival.
   *
   * The convenience is real — being asked for money and having to tap twice
   * to say you have already sent it is a small insult — but the obvious way
   * to build it is a GET that claims, and that is unsafe. Mail security
   * scanners (Defender SafeLinks, Proofpoint, Barracuda) fetch every URL in a
   * message before the recipient ever sees it, so a claiming GET gets fired
   * by a machine: their reminders stop, the roster tells the manager "says
   * sent", and nobody notices because a false claim looks exactly like a real
   * one.
   *
   * Claiming from JavaScript closes that hole. A scanner fetches the HTML
   * shell and runs none of this, so only a real browser — a real person —
   * ever reaches the POST. The button below is the `<noscript>` path in
   * practice, and the undo beside it is the way back.
   *
   * The ref, not `claim.isPending`: React 18 runs effects twice in
   * development and the flag is still false on the second pass, which claimed
   * twice. Harmless here, since claiming is idempotent server-side — but the
   * second request is a POST fired for no reason, and the same shape bit us
   * once already on the new-campaign button.
   */
  const claimedFromLink = useRef(false)
  const wantsClaim = params.get('sent') === '1'
  const pending = page.data?.status === 'open'
  // The ref only lasts as long as this page does, and `?sent=1` lasts for
  // ever — it is in the email. So the undo used to survive exactly until the
  // person opened that mail again, at which point a fresh page with a fresh
  // ref found the line open and claimed it straight back. The server decides
  // instead, because only the server remembers across visits.
  const mayAutoClaim = page.data?.can_auto_claim ?? true

  useEffect(() => {
    if (!wantsClaim || !pending || !mayAutoClaim || claimedFromLink.current) {
      return
    }
    claimedFromLink.current = true
    claim.mutate()
  }, [wantsClaim, pending, mayAutoClaim, claim])

  if (page.isPending) {
    return (
      <Centered>
        <CircularProgress size={22} />
      </Centered>
    )
  }

  if (page.error) {
    // A link that is genuinely dead. Sign-in and wrong-account are no longer
    // errors at all — they come back as a described campaign with a flag, so
    // the person can see what they were sent before deciding what to do.
    return (
      <Centered>
        <Paper sx={{ p: 3, maxWidth: 380 }}>
          <PublicHeader />
          <Typography variant="h2" sx={{ mb: 1 }}>
            This link does not work
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {(page.error as ApiError).detail ||
              'It may have expired, or the campaign may have been cancelled. ' +
                'Ask whoever sent it for a fresh one.'}
          </Typography>
        </Paper>
      </Centered>
    )
  }

  const pay = page.data


  return (
    <Centered>
      <Paper sx={{ p: 3, width: '100%', maxWidth: 400 }}>
          <PublicHeader />
        {/* One header, told in the tense the bill is actually in.
          *
          * It used to say "You owe Ana · $0.00 · due 3 Sept" on a settled
          * bill: wrong on all three counts at once. You do not owe it, zero is
          * not the number worth showing, and a due date on something already
          * paid is noise. The layout does not change — the words do. */}
        <Typography variant="caption">
          {pay.settled ? 'Settled with' : 'You owe'} {pay.fronter_name}
        </Typography>

        <Typography
          sx={{
            fontSize: '2.5rem',
            fontWeight: 680,
            letterSpacing: '-0.035em',
            lineHeight: 1.1,
            mt: 0.5,
          }}
        >
          {money(pay.settled ? pay.amount_cents : pay.outstanding_cents)}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          for {pay.label}
          {!pay.settled &&
            pay.due_date &&
            ` · due ${shortDate(pay.due_date)} (${relativeDays(pay.due_date)})`}
        </Typography>

        {pay.paid_cents > 0 && !pay.settled && (
          <Alert severity="info" sx={{ mt: 2, fontSize: '0.8125rem' }}>
            {money(pay.paid_cents)} of {money(pay.amount_cents)} already
            received. This is what is still open.
          </Alert>
        )}

        {pay.settled ? (
          <>
            <Alert
              icon={<CheckCircleIcon fontSize="small" />}
              severity="success"
              sx={{ mt: 2.5, fontSize: '0.8125rem' }}
            >
              Settled. Nothing more to do.
            </Alert>

            {/* The way back, and the reason the one-click link is safe to
                offer at all.

                Somebody who followed the link by accident, or who meant to
                read the page and not to settle, must not have to find the
                person they owe and ask them to reverse it. It rests on their
                word, so it is theirs to take back — and taking it back writes
                the mirror-image ledger event rather than deleting anything. */}
            <Button
              fullWidth
              size="small"
              disabled={unclaim.isPending}
              onClick={() => unclaim.mutate()}
              sx={{ mt: 0.5 }}
            >
              Didn&apos;t mean to — I haven&apos;t sent it
            </Button>
          </>
        ) : (
          <>
            {/* The claim, above the instructions rather than below them.

                It used to sit under the note and the reference code, which
                put the only control on the page below the fold on a phone.
                The order still matters though — this button does not take
                money, it records "I sent it" — so the instructions follow
                immediately, and the caption under the button says so. Anybody
                who taps it before paying can untap it. */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 2.5, minHeight: 44 }}
              disabled={claim.isPending}
              onClick={() => claim.mutate()}
            >
              I&apos;ve sent it
            </Button>

            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, textAlign: 'center' }}
            >
              This settles it and stops the reminders.
            </Typography>


            <Divider sx={{ my: 2.5 }} />

            <Typography variant="caption">How to pay {pay.fronter_name}</Typography>

            {/* The note, rendered as HTML.
             *
             * Safe because `richtext` escapes everything on save and only
             * linkifies bare URLs — so what is stored is already inert, and
             * this is where a "venmo.com/u/alex" written by the organiser
             * becomes something a person can tap. Do not widen this into an
             * HTML allowlist.
             *
             * This is the payment instruction until Stripe. Nothing else on
             * the page tells somebody where to send money. */}
            {pay.description ? (
              <Box
                sx={{
                  mt: 0.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: tokens.surface,
                  border: `1px solid ${tokens.line}`,
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  '& a': { color: 'primary.main' },
                  '& p': { margin: 0 },
                }}
                dangerouslySetInnerHTML={{ __html: pay.description }}
              />
            ) : (
              <Alert severity="warning" sx={{ mt: 0.5, fontSize: '0.8125rem' }}>
                {pay.fronter_name} has not said where to send it yet. Ask them
                before you pay — this page cannot tell you.
              </Alert>
            )}

            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Put <strong>{pay.reference_code}</strong> in the note so they know
              it was you.
            </Typography>

          </>
        )}

        {/* The web form the A2P registration points at.
          *
          * It exists because a manager pasting a friend's number is not that
          * friend agreeing, and carrier rules want consent from the person
          * being messaged, before the first message. STOP is a separate
          * obligation and does not stand in for this.
          *
          * On the pay page rather than anywhere else because the token
          * identifies exactly one person: whoever is holding this link is the
          * person the number belongs to, which is what makes the record worth
          * anything. A manager could not do this on their behalf if they
          * tried. */}
        {/* Asking for a number belongs where somebody joins, not here.
          *
          * This page is reached from an EMAIL, so it was asking a person who
          * has just been mailed to opt into texts, in the middle of trying to
          * pay. The number is now collected when they join — signed in, by
          * the person themselves, before anything is sent — which is both a
          * better moment and stronger evidence for the A2P registration than
          * a number typed halfway through settling up.
          *
          * What stays is the way OUT. Somebody who consented before this
          * changed can still see the number we hold and stop it, and STOP by
          * reply keeps working regardless — carriers require that and it is
          * not ours to design away. */}
        {!pay.settled && pay.sms_number && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Stack spacing={0.5}>
              <Typography variant="body2">
                We&apos;ll text you at <strong>{pay.sms_number}</strong>.
              </Typography>
              <Button
                size="small"
                disabled={optOut.isPending}
                onClick={() => optOut.mutate()}
                sx={{ alignSelf: 'flex-start' }}
              >
                Stop texting me
              </Button>
            </Stack>
          </>
        )}

        {/* A way out that is proportionate to the problem.
          *
          * There is no ceiling on how many messages a schedule sends, so
          * somebody being chased three times a week needs something short of
          * unsubscribing from every email we would ever send them. It stops
          * the chasing and nothing else — said plainly, because a button that
          * looked like it made the bill go away would be used that way. */}
        {!pay.settled && (
          <>
            <Divider sx={{ my: 2.5 }} />
            {/* Both sentences share one column, and the button sits beside
              * that column rather than beside the first sentence. They used
              * to be siblings of different parents: the first was squeezed
              * into whatever the button left over and wrapped after five
              * words, while the second ran the full width underneath. Two
              * lines of one thought, set to two different measures. */}
            {/* Always a column, never a row.
              *
              * Beside the button the sentences got about 300px of a 700px
              * card and wrapped after four words, with seventy pixels of
              * nothing to the right of the button — a paragraph squeezed
              * against a control that did not need the room. The phone
              * already stacked them and read better for it. */}
            <Stack alignItems="flex-start" spacing={1} useFlexGap>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  {/* Says what happens, not "too many messages?" — which asked
                      a question without naming what would stop, or for how
                      much. The button now describes itself, this line describes
                      what is happening now. Nothing here mentions replying
                      STOP: that is a text-message control, and the section
                      above already offers it — but only to people who actually
                      receive texts. Explaining it to somebody reading an email
                      link was answering a question they had not asked. */}
                  {pay.muted
                    ? 'Reminders are off for this bill. You still owe it.'
                    : 'We will keep reminding you until this is settled.'}
                </Typography>
                {!pay.muted && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Only this bill, and it does not change what you owe.
                  </Typography>
                )}
              </Box>
              <Button
                size="small"
                onClick={() => mute.mutate(!pay.muted)}
                disabled={mute.isPending}
                // A text button carries horizontal padding of its own, and
                // stacked under the sentences it describes that padding is
                // the only thing between its label and their left edge.
                // Removed outright rather than cancelled with a negative
                // margin, which left it a couple of pixels out.
                // 12px to match the two lines above it, not the 13px a
                // small Button defaults to. Sitting in a row it read as a
                // separate control and the difference did not show; stacked
                // under the sentences it describes, a size and a half-weight
                // apart looked like two different fonts. The weight stays —
                // it is still the action, and the only one here.
                sx={{ flexShrink: 0, px: 0, minWidth: 0, fontSize: '0.75rem' }}
              >
                {pay.muted
                  ? 'Turn reminders back on'
                  : 'Stop reminders for this bill'}
              </Button>
            </Stack>

            {/* Its own line, below a rule, because it is not a preference.
                Everything above adjusts how much we chase somebody who owes
                the money; this says they do not. Sitting it under the mute
                made it read as a third volume setting, and "I don't know what
                this is" is a sentence nobody scans as a control at all.

                Named for the consequence instead: it tells the organiser they
                have the wrong person, which is the thing that has to happen —
                a mistyped address puts a stranger on a demand for money, and
                muting alone leaves the line looking like somebody slow to pay
                and gets them chased harder.

                Quiet on purpose. Most people reading this page do owe it, and
                a stray tap sends the organiser chasing a problem that does
                not exist. */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              {pay.not_mine ? (
                <>
                  You told {pay.fronter_name} this is not you, and the messages
                  have stopped.{' '}
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={() => notMine.mutate(false)}
                    sx={{ font: 'inherit', verticalAlign: 'baseline' }}
                  >
                    Undo
                  </MuiLink>
                </>
              ) : (
                <>
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={() => notMine.mutate(true)}
                    disabled={notMine.isPending}
                    sx={{ font: 'inherit', verticalAlign: 'baseline' }}
                  >
                    This is not me
                  </MuiLink>
                  {/* "we will tell", not "tell": the app does it. Written as
                    * an instruction, it read as homework — go and find the
                    * person chasing you and sort it out yourself — which is
                    * the opposite of what the control does, and disagreed
                    * with the sentence it turns into once tapped. */}
                  {` — we will tell ${pay.fronter_name} they have the wrong
                    person, and stop the messages.`}
                </>
              )}
            </Typography>
          </>
        )}

        {/* Everyone on the campaign, redacted.
          *
          * First names and a tick, never an amount — this page's only
          * credential is the link, and a link gets forwarded. The count on
          * its own never answered the question people actually have, which
          * is whether they are the last one holding it up.
          *
          * Two states. There used to be a third — a lighter tick for "says
          * they sent it" — and it went when the status did: saying so is
          * what settles a line now. */}
        {pay.roster.length > 1 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              {pay.social_proof ? `${pay.social_proof} have paid` : 'Who else is on this'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {pay.roster.map((row, index) => (
                <Box
                  key={index}
                  title={row.settled ? 'Settled' : 'Not yet'}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.375,
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    fontSize: '0.8125rem',
                    border: `1px solid ${tokens.line}`,
                    bgcolor: row.is_you ? tokens.surface : 'transparent',
                    fontWeight: row.is_you ? 620 : 400,
                    color: row.settled ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {row.settled && (
                    <CheckCircleIcon
                      sx={{ fontSize: 13, color: 'success.main' }}
                    />
                  )}
                  {row.name}
                  {row.is_you && ' (you)'}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* No social proof for a roster of one.
          *
          * The branch above needs somebody else on the list to be persuasive
          * — "3 of 8 have paid" works because seven other people exist. With
          * only you on it, the same line renders "0 of 1 have paid.": a
          * sentence that tells the one person reading it that they have not
          * paid, which they know, centred on a card where nothing else is. */}
      </Paper>

      {/* Everything else this person owes, under the bill they were sent.
        *
        * The link they followed answers one question — "what is this $75?" —
        * and leaves the one they actually arrived with, "what do I owe?",
        * unanswered until somebody sends them another email. The server
        * decides what belongs here: `identity.same_human` joins person rows
        * only on PROVEN addresses, so a contact a manager typed in sees the
        * bills that manager sent them and nothing else. */}
      {pay.other_bills.length > 0 && (
        <Paper sx={{ p: { xs: 2.5, sm: 3 }, mt: 2 }}>
          {/* "Your other bills", not "You also owe": a settled one belongs
            * here too — seeing that last month's is done is half the reason
            * to look — and "You also owe" printed above the word "Settled"
            * says two opposite things in one glance. */}
          <Typography variant="caption" sx={{ display: 'block', mb: 1.5 }}>
            Your other bills
          </Typography>

          <Stack divider={<Divider />} spacing={0}>
            {pay.other_bills.map((bill) => (
              <Stack
                key={bill.pay_token}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ py: 1.25 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <MuiLink
                    href={`/pay/${bill.pay_token}`}
                    sx={{ fontWeight: 560, display: 'block' }}
                  >
                    {bill.label}
                  </MuiLink>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {bill.fronter_name} · due {shortDate(bill.due_date)}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 620,
                    whiteSpace: 'nowrap',
                    color: bill.settled ? 'text.secondary' : 'text.primary',
                  }}
                >
                  {bill.settled ? 'Settled' : money(bill.outstanding_cents)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}
    </Centered>
  )
}

/**
 * Every state of this page, footer included.
 *
 * The footer lives here rather than beside the card so that the states a
 * stranger is most likely to be confused by — wrong account, dead link — say
 * who is asking, just as the working one does. Somebody holding a broken link
 * from an unfamiliar sender is exactly who needs a name and a way to write in.
 */
function Centered({ children }: { children: React.ReactNode }) {
  /**
   * A way out, but only for somebody who has one.
   *
   * This page has no nav on purpose — most people who see it arrived from an
   * email, have never heard of us, and need one number and one button. But it
   * is also reachable from your own dashboard when you owe on a campaign, and
   * that route left you with nowhere to go but Back.
   *
   * `useMe` never blocks the render: it is already cached when you came from
   * the dashboard, and for a stranger it resolves to signed_in false in the
   * background while the page is on screen. `/auth/me` answers 200 either way,
   * so being signed out is an answer here rather than an error.
   */
  const me = useMe()

  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        alignContent: 'center',
        minHeight: '100dvh',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      {me.data?.signed_in && (
        <Box sx={{ width: '100%', maxWidth: 400, mb: 1.5 }}>
          <Link
            component={RouterLink}
            to="/"
            variant="caption"
            underline="hover"
            sx={{ color: 'text.secondary' }}
          >
            ← Your campaigns
          </Link>
        </Box>
      )}
      {children}
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Footer />
      </Box>
    </Box>
  )
}
